import React, { useEffect } from "react";
import { useParams } from "react-router-dom";
import { Task, TaskUser } from "./types";
import { useStore } from "../../helpers";
import { TailwindModal } from "../../components/TailwindModal";
import { DebouncedUserAutocomplete } from "./components/DebouncedUserAutocomplete";
import { FTPInput } from "../../components-new/FTPInput";
import Button from "@mui/material/Button";
import {
    useForm,
    FormProvider,
    useWatch,
    UseFormReturn,
} from "react-hook-form";

interface AssignTaskModalProps {
    isOpen: boolean;
    onClose: () => void;
    task: Task;
    onAssign: (userId: number, interviewCount: number) => void;
}

interface UserSelectionFieldProps {
    users: TaskUser[];
    projectid: string | undefined;
    methods: UseFormReturn<{
        userId: TaskUser | null;
        interviewCount: string;
    }>;
}

const UserSelectionField: React.FC<UserSelectionFieldProps> = ({
    users,
    projectid,
    methods,
}) => {
    const { projectStore } = useStore();
    const userId = useWatch({
        control: methods.control,
        name: "userId",
    });

    const error = methods.formState.errors.userId;

    // Async search function using API
    const handleSearch = async (query: string): Promise<TaskUser[]> => {
        if (!projectid) {
            return users;
        }

        const payload = {
            projectid: parseInt(projectid),
            tags: [],
            query: query,
            limit: 500,
            offset: 0,
        };

        try {
            const res = await projectStore.getAllUsers(payload);
            if (res.status === 200 && res.data?.users) {
                return res.data.users || [];
            }
            return [];
        } catch (error) {
            console.error("Error searching users:", error);
            return [];
        }
    };

    return (
        <DebouncedUserAutocomplete
            value={userId}
            onChange={(user) => {
                methods.setValue("userId", user, { shouldValidate: true });
                if (error) {
                    methods.clearErrors("userId");
                }
            }}
            debounceDelay={600}
            onSearch={handleSearch}
            label="Assign To"
            placeholder="Search and select user..."
            error={!!error}
            helperText={error?.message}
        />
    );
};

export const AssignTaskModal: React.FC<AssignTaskModalProps> = ({
    isOpen,
    onClose,
    task,
    onAssign,
}) => {
    const { projectid } = useParams<{ projectid: string }>();
    const { taskManagerStore } = useStore();
    const { users } = taskManagerStore;

    const methods = useForm({
        defaultValues: {
            userId: task.user || null,
            interviewCount: String(task.count?.count || ""),
        },
    });

    // Reset form when task changes or modal opens
    useEffect(() => {
        if (isOpen) {
            methods.reset({
                userId: task.user || null,
                interviewCount: String(task.count?.count || ""),
            });
        }
    }, [task, isOpen, methods]);

    const handleAssign = (data: {
        userId: TaskUser | null;
        interviewCount: string;
    }) => {
        if (!data.userId) {
            methods.setError("userId", {
                type: "manual",
                message: "Please select a user",
            });
            return;
        }

        const count = parseInt(data.interviewCount);
        if (!data.interviewCount || isNaN(count) || count <= 0) {
            methods.setError("interviewCount", {
                type: "manual",
                message: "Please enter a valid number of interviews",
            });
            return;
        }

        onAssign(data.userId.id, count);
    };

    const handleClose = () => {
        methods.reset();
        onClose();
    };

    return (
        <TailwindModal isOpen={isOpen} onClose={handleClose}>
            <FormProvider {...methods}>
                <form
                    onSubmit={methods.handleSubmit(handleAssign)}
                    className="bg-white rounded-xl shadow-xl p-6 max-w-md w-full mx-auto"
                >
                    <h2 className="text-lg font-semibold text-slate-900 mb-4">
                        {task.node?.display || task.more?.name || "Untitled"} -{" "}
                        {task.node?.piv_level1 || "—"}
                    </h2>

                    <div className="space-y-4">
                        {/* User Selection */}
                        <UserSelectionField
                            users={users}
                            projectid={projectid}
                            methods={methods}
                        />

                        {/* Interview Count */}
                        <FTPInput
                            name="interviewCount"
                            label="Number of Interviews"
                            type="number"
                            numericOnly
                        />
                    </div>

                    {/* Footer Buttons */}
                    <div className="mt-6 pt-4 border-t border-slate-200 flex items-center gap-3">
                        <Button
                            type="button"
                            onClick={handleClose}
                            variant="outlined"
                            fullWidth
                            size="large"
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            variant="contained"
                            color="primary"
                            fullWidth
                            size="large"
                        >
                            Assign
                        </Button>
                    </div>
                </form>
            </FormProvider>
        </TailwindModal>
    );
};
